import { Icon } from "../Icon";
import { Button } from "../Button";
import { AlertType } from "../Alert";
import { Checkbox } from "../Checkbox";
import {
  ValueOf,
  PROJECT_CONDITION,
} from "src/domains/shared/constants/constants";
import { Box, BoxProps, Typography } from "@mui/material";
import {
  forwardRef,
  ReactNode,
  DragEvent,
  useState,
  useRef,
  useEffect,
  memo,
} from "react";
import { TYPOGRAPHY_SCALE, COLOR_CONSTANTS } from "../../constants/styles";
import useLocale from "src/domains/shared/hooks/localeHook/useLocale";
import { useTranslation } from "react-i18next";

interface AlertProps {
  alertTitle: string;
  alertMessage: string;
}

const Alert = ({ alertTitle, alertMessage }: AlertProps) => {
  return (
    <Box sx={alert}>
      <Icon name="etc/WarningAmber" size={20} color="#ff9800" minWidth={20} />
      <Box sx={alertTextContainer}>
        <Typography sx={alertTitleText}>{alertTitle}</Typography>
        <Typography sx={alertMessageText}>{alertMessage}</Typography>
      </Box>
    </Box>
  );
};

const alert = {
  padding: "14px 16px",
  display: "flex",
  gap: "12px",
  backgroundColor: COLOR_CONSTANTS._components.alert.warning.background,
} as const;

const alertTextContainer = {
  display: "flex",
  flexDirection: "column",
  gap: 1,
  flex: 1,
  minWidth: 0,
} as const;

const alertTitleText = {
  ...TYPOGRAPHY_SCALE.alertTitle,
  wordWrap: "break-word",
  whiteSpace: "normal",
} as const;

const alertMessageText = {
  ...TYPOGRAPHY_SCALE.body2,
  wordWrap: "break-word",
  whiteSpace: "normal",
} as const;

/**
 * 파일 목록 컨테이너 props — 드래그 앤 드롭 업로드 지원.
 *
 * `FileList.Container` / `FileList.Item` / `FileList.Empty` 복합 컴포넌트 패턴.
 * 컨테이너 영역에 운영체제 파일을 드래그 오버 시 파란 테두리 시각적 피드백 표시.
 *
 * `onFileDrop` 은 단순 파일 드롭, `onItemsDrop` 은 폴더 포함 드롭(`DataTransferItemList`).
 * 파일(`dataTransfer.types` 의 `Files`)이 실리지 않은 드래그 — 목록 안 행을 폴더로 옮기는 드래그 등 — 는
 * 강조도 업로드도 하지 않고 지나보낸다(그 드래그의 드롭 대상이 따로 처리한다).
 */
export interface FileListProps extends BoxProps {
  fileListLength: number;
  scroll?: boolean;
  alert?: boolean;
  alertMessage?: string;
  alertTitle?: string;
  alertType?: AlertType;
  onFileDrop?: (files: FileList) => void;
  onItemsDrop?: (items: DataTransferItemList) => void;
  children: ReactNode;
}

export const Container = forwardRef<HTMLDivElement, FileListProps>(
  (
    {
      sx = {},
      fileListLength = 0,
      children,
      scroll = false,
      alert = false,
      alertType = "warning",
      alertMessage = "message",
      alertTitle = "Title",
      onFileDrop,
      onItemsDrop,
      ...props
    },
    ref
  ) => {
    const [isDragOver, setIsDragOver] = useState(false);

    const containerStyle =
      fileListLength === 0
        ? noFileContainer
        : scroll
        ? containerWithScroll
        : containerWithoutScroll;

    const dragOverStyle = isDragOver
      ? {
          opacity: 0.5,
          borderRadius: "8px",
          border: "2px dashed #007bff",
        }
      : {};

    const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
      if (!carriesFiles(e)) return;
      e.preventDefault();
      e.stopPropagation();
      setIsDragOver(true);
    };

    const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
      if (!carriesFiles(e)) return;
      e.preventDefault();
      e.stopPropagation();
      setIsDragOver(false);
    };

    const handleDrop = (e: DragEvent<HTMLDivElement>) => {
      if (!carriesFiles(e)) return;
      e.preventDefault();
      e.stopPropagation();
      setIsDragOver(false);

      if (onItemsDrop) {
        onItemsDrop(e.dataTransfer.items);
      } else {
        const files = e.dataTransfer.files;
        if (files.length > 0 && onFileDrop) {
          onFileDrop(files);
        }
      }
    };

    return (
      <Box
        ref={ref}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        sx={{ ...containerStyle, ...dragOverStyle, ...sx }}
        {...props}
      >
        {alert && fileListLength > 0 && (
          <Alert alertTitle={alertTitle} alertMessage={alertMessage} />
        )}
        {children}
      </Box>
    );
  }
);

Container.displayName = "Container";

/** 운영체제 파일 드래그인가 — `dragover` 에서는 파일 내용을 못 읽어도 `types` 는 읽힌다. */
const carriesFiles = (event: DragEvent<HTMLElement>) =>
  Array.from(event.dataTransfer?.types ?? []).includes("Files");

const containerWithScroll = {
  width: "100%",
  height: "100%",
  border: "2px solid transparent",
  padding: "6px",
  display: "flex",
  gap: "4px",
  flexDirection: "column",
  overflowY: "auto",
  boxSizing: "border-box",
  "&::-webkit-scrollbar": {
    width: "15px",
  },
  "&::-webkit-scrollbar-thumb": {
    width: "5px",
    padding: "0",
    border: "5px solid " + COLOR_CONSTANTS._native["scrollbar-bg"],
    borderRadius: "10px",
    backgroundColor: COLOR_CONSTANTS._components.rating.enabledBorder,
  },
  "&::-webkit-scrollbar-track": {
    backgroundColor: COLOR_CONSTANTS._native["scrollbar-bg"],
  },
} as const;

const containerWithoutScroll = {
  width: "100%",
  height: "100%",
  padding: "6px",
  borderRadius: "8px",
  border: "2px solid transp`a`rent",
  display: "flex",
  gap: "4px",
  flexDirection: "column",
  overflowY: "auto",
  boxSizing: "border-box",
  "&::-webkit-scrollbar": {
    display: "none",
  },
  scrollbarWidth: "none",
  msOverflowStyle: "none",
} as const;

const noFileContainer = {
  ...containerWithoutScroll,
  alignItems: "center",
  justifyContent: "center",
  gap: "24px",
  overflow: "hidden",
} as const;

const emptyContainer = {
  flex: 1,
  minWidth: 0,
  minHeight: "90px",
  height: "100%",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  gap: "8px 0",
  overflow: "hidden",
  padding: "8px",
} as const;

const textContainer = {
  width: "100%",
  padding: "8px",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  gap: "8px",
} as const;

const textarea = {
  width: "100%",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  wordWrap: "break-word",
} as const;

/** `FileList.Container` 자식으로 빈 상태를 표시하는 placeholder props. */
export interface EmptyProps {
  fileType: string;
  showBottom?: boolean;
  onClick?: () => void;
}

const buttonContainer = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "8px",
} as const;

const button = {
  maxWidth: "65px",
  maxHeight: "30px",
  fontSize: "12px",
} as const;

const Empty = ({ fileType, showBottom = false, onClick }: EmptyProps) => {
    const { t } = useTranslation("projects");
  const [locale] = useLocale();
  const currentLocale = locale.locale as "ko" | "en";

  return (
    <Box sx={emptyContainer}>
      <Box sx={textContainer}>
        <Icon name="etc/upload" size={20} />
        <Box sx={textarea}>
          <Typography
            sx={{
              ...TYPOGRAPHY_SCALE.body2,
              whiteSpace: "normal",
              textAlign: "center",
            }}
            color="textDisabled"
          >
            {fileType} {t("fileList.dragAndDrop", { ns: "projects" })}
          </Typography>
        </Box>
        {showBottom && (
          <Typography sx={TYPOGRAPHY_SCALE.body2} color="textDisabled">
            {t("fileList.or", { ns: "projects" })}
          </Typography>
        )}
      </Box>
      {showBottom && (
        <Box sx={buttonContainer}>
          <Button
            sx={button}
            size="medium"
            intent="primary"
            emphasis="contained"
            onClick={onClick}
          >
            {t("fileList.selectFile", { ns: "projects" })}
          </Button>
        </Box>
      )}
    </Box>
  );
};

Empty.displayName = "Empty";

const AnimatedText: React.FC<{ text: string }> = memo(({ text }) => {
  const textRef = useRef<HTMLSpanElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [needsAnimation, setNeedsAnimation] = useState(false);

  useEffect(() => {
    if (textRef.current && containerRef.current) {
      const containerWidth = containerRef.current.offsetWidth;
      const textWidth = textRef.current.scrollWidth;

      setNeedsAnimation(textWidth > containerWidth);
    }
  }, [text]);

  return (
    <Box
      ref={containerRef}
      sx={{
        position: "relative",
        overflow: "hidden",
        width: "100%",
        ...(needsAnimation && {
          "&:hover .animated-text": {
            animation: "flowText 5s linear infinite",
          },
        }),
      }}
    >
      <Typography
        ref={textRef}
        className={needsAnimation ? "animated-text" : ""}
        sx={{
          ...itemName,
          ...(needsAnimation && {
            "&:hover": {
              textOverflow: "unset",
              overflow: "visible",
              whiteSpace: "nowrap",
            },
          }),
        }}
      >
        {text}
      </Typography>
    </Box>
  );
});

AnimatedText.displayName = "AnimatedText";

/** `FileList.Container` 자식으로 한 줄을 표시하는 row props. `size` 는 미리 포맷된 문자열(예: `"3.2 MB"`)이며, 미지정 시 크기 영역을 렌더하지 않는다(예: 수정 모드에서 숨김). */
export interface ItemProps extends Omit<BoxProps, "onClick"> {
  index: number;
  checked: boolean;
  name: string;
  size?: string;
  selected?: boolean;
  status?: ValueOf<typeof PROJECT_CONDITION>;
  onCheckBoxClick: ({
    name,
    checked,
  }: {
    name: string;
    checked: boolean;
  }) => void;
  onItemClick?: ({ name }: { name: string }) => void;
  children?: ReactNode;
  /** 이름 영역 대체 렌더(예: 인라인 rename input). 미지정 시 `name` 텍스트가 렌더된다 — `name` 은 클릭/체크 식별자이자 체크박스의 접근명이라 여전히 필수. */
  nameSlot?: ReactNode;
  /**
   * 부분 선택 표시. 자기 부분트리의 일부만 선택된 부모 행(묶음·폴더)이 쓴다 — 그 상태를
   * `checked`/빈 상태 중 하나로 뭉개면 행이 거짓말을 한다. 미지정이면 오늘과 같다.
   * 화면의 「−」와 함께 `aria-checked="mixed"` 로 나가므로 낭독도 같은 상태를 듣는다.
   */
  indeterminate?: boolean;
}

const Item = memo(
  forwardRef<HTMLDivElement, ItemProps>(
    (
      {
        checked,
        name,
        size,
        index,
        selected = false,
        onCheckBoxClick,
        onItemClick,
        sx,
        status,
        children,
        nameSlot,
        indeterminate,
        ...props
      },
      ref
    ) => {
      const backgroundColor = selected
        ? COLOR_CONSTANTS.primary._states.selected
        : index % 2 === 0
        ? "white"
        : "grey.50";

      function handleCheckBoxClick({
        name,
        checked,
      }: Pick<ItemProps, "name" | "checked">) {
        onCheckBoxClick({ name, checked: !checked });
      }

      function handleItemClick(name: string) {
        onItemClick?.({ name });
      }

      return (
        <Box
          ref={ref}
          sx={{ ...item, backgroundColor, ...sx }}
          onClick={() => handleItemClick(name)}
          {...props}
        >
          {/* nameSlot(인라인 편집 input 등)은 이름이 쓰던 가로 공간 전체를 차지하도록 좌측 영역을 늘린다 */}
          <Box sx={{ ...itemLeftSection, ...(nameSlot && { flex: 1 }) }}>
            <Checkbox
              size="small"
              checked={checked}
              indeterminate={indeterminate}
              onChange={() => handleCheckBoxClick({ name, checked })}
              onClick={(e) => e.stopPropagation()}
              /*
                행 이름이 곧 체크박스의 접근명이다 — 옆의 이름은 형제 요소일 뿐이라, 이름이 없으면
                낭독에 「checkbox, not checked」만 남아 어느 줄을 고르는지 사라진다. 부분 선택은
                `aria-checked="mixed"` 로만 전해진다: MUI 는 네이티브 input 을 indeterminate 로
                두지 않고 `data-indeterminate` 만 붙이는데 그 속성은 접근성 트리에 닿지 않는다.
              */
              slotProps={{
                input: {
                  "aria-label": name,
                  "aria-checked": indeterminate ? "mixed" : undefined,
                },
              }}
            />
            {nameSlot ?? <AnimatedText text={name} />}
          </Box>
          <Box sx={itemRightSection}>
            {size !== undefined && (
              <Typography sx={TYPOGRAPHY_SCALE.buttonMedium}>{size}</Typography>
            )}
            {status && (
              <Box sx={{ ...dot, backgroundColor: statusColor[status] }}></Box>
            )}
            {children}
          </Box>
        </Box>
      );
    }
  )
);

Item.displayName = "Item";

const item = {
  width: "100%",
  height: "24px",
  display: "flex",
  alignItems: "center",
  gap: "8px",
  justifyContent: "space-between",
  flexShrink: 0,
  overflow: "hidden",
  cursor: "pointer",
} as const;

const itemLeftSection = {
  display: "flex",
  alignItems: "center",
  paddingRight: "8px",
  overflow: "hidden",
} as const;

const itemName = {
  ...TYPOGRAPHY_SCALE.buttonMedium,
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  "@keyframes flowText": {
    "0%": {
      transform: "translateX(0)",
    },
    "100%": {
      transform: "translateX(-100%)",
    },
  },
} as const;

const itemRightSection = {
  display: "flex",
  alignItems: "center",
  gap: "8px",
} as const;

const dot = {
  width: "8px",
  height: "8px",
  borderRadius: "50%",
} as const;

/** 파일 처리 상태의 점 색 — 검수 모드의 `review_2d_ready` 처럼 파일에 없는 상태는 색을 두지 않는다. */
const statusColor: Partial<Record<ValueOf<typeof PROJECT_CONDITION>, string>> =
  {
    before: "rgba(0, 0, 0, 0.38)",
    requested: "rgba(0, 0, 0, 0.38)",
    on_processing: "#03a9f4",
    failed: "#ef5350",
    reviewing: "#ff9800",
    completed: "#4caf50",
  };

const FileList = {
  Container,
  Empty,
  Item,
};

export { FileList };
